import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ConfirmModal, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { usePermissions } from '@/hooks/usePermissions';
import { api } from '@/lib/api';
import { h5pSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import {
  type H5pPackage,
  packageCourseCount,
  packageLibraryIcon,
  packageLibraryLabel,
  packageMachineName,
  packageStatusLabel,
  packageStatusModifier,
  packageVersionCount,
} from './h5p-package-utils';

interface H5pPackageCardProps {
  pkg: H5pPackage;
}

export function H5pPackageCard({ pkg }: H5pPackageCardProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.H5P_EDITAR);
  const canDelete = hasPermission(PERMISSIONS.H5P_ELIMINAR);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const courseCount = packageCourseCount(pkg);
  const inUse = courseCount > 0;
  const machineName = packageMachineName(pkg);
  const icon = packageLibraryIcon(machineName);
  const statusModifier = packageStatusModifier(pkg.status);

  const deleteMutation = useMutation({
    mutationFn: () => api(`/h5p/packages/${pkg.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleteOpen(false);
      toast(h5pSuccessToast('delete'));
      void queryClient.invalidateQueries({ queryKey: ['h5p'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el H5P'));
    },
  });

  return (
    <article className="lms-h5p-card group">
      <div className="lms-h5p-card__body">
        <div className="lms-h5p-card__top">
          <span className={`lms-h5p-card__status lms-h5p-card__status--${statusModifier}`}>
            {packageStatusLabel(pkg.status)}
          </span>

          {(canEdit || canDelete) && (
          <div className="lms-h5p-card__actions">
            {canEdit && (
              <Link
                to={`/h5p/${pkg.id}/edit`}
                className="lms-h5p-card__icon-btn"
                aria-label="Editar"
                title="Editar"
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  edit
                </span>
              </Link>
            )}
            {canDelete && inUse ? (
              <span
                className="lms-h5p-card__icon-btn lms-h5p-card__icon-btn--disabled"
                title="No se puede eliminar mientras esté en uso"
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  delete
                </span>
              </span>
            ) : canDelete ? (
              <button
                type="button"
                className="lms-h5p-card__icon-btn lms-h5p-card__icon-btn--danger"
                aria-label="Eliminar"
                title="Eliminar"
                onClick={() => setDeleteOpen(true)}
              >
                <span className="material-symbols-outlined" aria-hidden="true">
                  delete
                </span>
              </button>
            ) : null}
          </div>
          )}
        </div>

        <div className="lms-h5p-card__main">
          <div className="lms-h5p-card__icon">
            {pkg.thumbnailUrl ? (
              <img src={pkg.thumbnailUrl} alt="" className="lms-h5p-card__thumb" />
            ) : (
              <span className="material-symbols-outlined" aria-hidden="true">
                {icon}
              </span>
            )}
          </div>
          <div className="lms-h5p-card__copy">
            <h3>
              <Link to={`/h5p/${pkg.id}`}>{pkg.title}</Link>
            </h3>
            <p>
              <span>v{packageVersionCount(pkg)}</span>
              <span className="lms-h5p-card__dot" aria-hidden="true" />
              <span className="lms-h5p-card__usage">
                <span className="material-symbols-outlined" aria-hidden="true">
                  linked_camera
                </span>
                {courseCount} {courseCount === 1 ? 'curso' : 'cursos'}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="lms-h5p-card__footer">
        <span className="lms-h5p-card__library">{packageLibraryLabel(pkg)}</span>
        <Link to={`/h5p/${pkg.id}`} className="lms-h5p-card__detail">
          Ver detalles
        </Link>
      </div>

      <ConfirmModal
        open={deleteOpen}
        title="Eliminar actividad H5P"
        description={`¿Está seguro de eliminar la actividad H5P «${pkg.title}»? Esta acción no se puede deshacer.`}
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate()}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleteOpen(false);
        }}
      />
    </article>
  );
}
